/**
 =========================================================
 * Material Dashboard 2 React - v2.2.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-react
 * Copyright 2023 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

import React, { useEffect, useState } from 'react';

// @mui material components
import Grid from "@mui/material/Grid";
import {isEmpty} from "../../../components/Utils/common";
import MDInput from "../../../components/MDInput";
import PropTypes from "prop-types";
import {Controller, useFormContext} from "react-hook-form";
import {FormControl, InputAdornment, InputLabel, Select, Typography, Divider} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import {useTranslation} from "react-i18next";
import useApi from "../../../components/Utils/api";
import { CURRENCIES } from "../../../utils";
import useAllowOnlyNumber from "../../../hooks/useAllowOnlyNumber";
import useAllowOnlyLetters from "../../../hooks/useAllowOnlyLetters";


function MerchantBankDetails({merchant, editmode, setMerchant, type}) {
  const {t} = useTranslation();
  const formMethods = useFormContext();
  const { register, formState: { errors }, control, setValue } = formMethods;
  const [banks, setBanks] = useState([]);
  const api = useApi();
  const handleNumberKeyDown = useAllowOnlyNumber();
  const handleLettersKeyDown = useAllowOnlyLetters();

  useEffect(() => {
    getAllBanksList();
  }, []);

  // Handler to update bank code when bank is selected
  const handleBankChange = (bankId, index) => {
    const selectedBank = banks.find(bank => bank.id === bankId);
    if (selectedBank && selectedBank.swiftCode) {
      setValue(`merchantBankDetails[${index}].bankCode`, selectedBank.swiftCode, { shouldValidate: false, shouldDirty: true });
    } else {
      setValue(`merchantBankDetails[${index}].bankCode`, '', { shouldValidate: false, shouldDirty: true });
    }
  };

  const getAllBanksList = () => {
    api.get("getAllBanks")
      .then(response => {
        // If your API returns { body: [...] } or { data: [...] }, adjust accordingly
        if (Array.isArray(response.data)) {
          setBanks(response.data);
        } else if (response.data && Array.isArray(response.data.body)) {
          setBanks(response.data.body);
        } else {
          setBanks([]);
        }
      })
      .catch(() => setBanks([]));
  };
  // console.log(errors);
  return (
      <Grid mt={3} mb={3} sx={{ maxWidth: "680px" }}>
        {errors?.merchantBankDetails?.message ? <Typography style={{ marginBottom: '15px' }} color="red" variant="subtitle2" component="h2">{errors?.merchantBankDetails?.message}</Typography> : <></>}
        <Grid container spacing={2}>

          <Grid item xs={12} sm={6}>
            <Controller
                name="merchantBankDetails[0].currency"
                control={control} // make sure to get "control" from useForm()
                defaultValue=""
                render={({ field }) => (
                    <MDInput
                        select
                        {...field}
                        value={field.value || ""}
                        label={t('currency')}
                        fullWidth
                        required
                        error={!!errors?.merchantBankDetails?.[0]?.currency}
                        helperText={errors?.merchantBankDetails?.[0]?.currency?.message}
                        className="internal-select"
                        disabled={!editmode}
                    >
                      <MenuItem value="">-{t('currency')}-</MenuItem>
                      {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </MDInput>
                )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="merchantBankDetails[0].bankName"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <MDInput
                  select
                  {...field}
                  value={field.value || ""}
                  label={t('settlement_bank_name')}
                  fullWidth
                  onChange={(e) => {
                    field.onChange(e);
                    handleBankChange(e.target.value, 0);
                  }}
                  required
                  error={!!errors?.merchantBankDetails?.[0]?.bankName}
                  helperText={errors?.merchantBankDetails?.[0]?.bankName?.message}
                  className="internal-select"
                  disabled={!editmode}
                >
                  <MenuItem value="">-{t('settlement_bank_name')}-</MenuItem>
                  {banks.map(bank => (
                    <MenuItem key={bank.id} value={bank.id}>{bank.bankName}</MenuItem>
                  ))}
                </MDInput>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDInput
                type="text"
                label={t('settlement_account_name')}
                name="merchantBankDetails[0].branch"
                {...register("merchantBankDetails[0].branch")}
                fullWidth
                required
                disabled={!editmode}
                onKeyDown={handleLettersKeyDown}
                error={errors?.merchantBankDetails?.[0]?.branch}
                helperText={errors?.merchantBankDetails?.[0]?.branch?.message}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDInput
                type="text"
                label={t('settlement_account_number')}
                name="merchantBankDetails[0].accountNumber"
                {...register("merchantBankDetails[0].accountNumber")}
                fullWidth
                required
                disabled={!editmode}
                onKeyDown={handleNumberKeyDown}
                InputProps={{
                  startAdornment: <InputAdornment position="start">AE</InputAdornment>,
                }}
                inputProps={{ maxLength: 25 }}
                error={errors?.merchantBankDetails?.[0]?.accountNumber}
                helperText={errors?.merchantBankDetails?.[0]?.accountNumber?.message}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="merchantBankDetails[0].bankCode"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <MDInput
                  type="text"
                  label={t('branch_code')}
                  {...field}
                  fullWidth
                  required
                  disabled={true}
                  error={errors?.merchantBankDetails?.[0]?.bankCode}
                  helperText={errors?.merchantBankDetails?.[0]?.bankCode?.message}
                />
              )}
            />
          </Grid>

          {/* Repeat for the second currency (ZiG) */}
          {/* <Grid item xs={2}>
            <FormControl fullWidth style={{height: '100%'}}>
              <InputLabel id="currency-simple-select-label">{t('currency')}</InputLabel>
              <Controller
                  name="merchantBankDetails[1].currency"
                  control={control} // make sure to get "control" from useForm()
                  defaultValue=""
                  render={({ field }) => (
                      <Select
                          labelId="currency-simple-select-label"
                          id="currency-simple-select"
                          label={t('currency')}
                          fullWidth
                          {...field}
                          error={errors?.merchantBankDetails?.[1]?.currency}
                          // helperText={errors?.merchantBankDetails?.[1]?.currency?.message}
                          style={{height: '100%'}}
                          disabled={!editmode}
                      >
                        <MenuItem value="">-{t('currency')}-</MenuItem>
                        {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                      </Select>
                  )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={3}>
            <FormControl fullWidth style={{height: '100%'}}>
              <InputLabel id="bankname-select-label-1">{t('settlement_bank_name')}</InputLabel>
              <Controller
                name="merchantBankDetails[1].bankName"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select
                    labelId="bankname-select-label-1"
                    id="bankname-select-1"
                    label={t('settlement_bank_name')}
                    fullWidth
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      handleBankChange(e.target.value, 1);
                    }}
                    error={errors?.merchantBankDetails?.[1]?.bankName}
                    style={{height: '100%'}}
                    disabled={!editmode}
                  >
                    <MenuItem value="">-{t('settlement_bank_name')}-</MenuItem>
                    {banks.map(bank => (
                      <MenuItem key={bank.id} value={bank.id}>{bank.bankName}</MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={2}>
            <MDInput
                type="text"
                label={t('settlement_account_name')}
                name="merchantBankDetails[1].branch"
                {...register("merchantBankDetails[1].branch")}
                fullWidth
                disabled={!editmode}
                onKeyDown={handleLettersKeyDown}
                error={errors?.merchantBankDetails?.[1]?.branch}
                helperText={errors?.merchantBankDetails?.[1]?.branch?.message}
            />
          </Grid>
          <Grid item xs={3}>
            <MDInput
                type="text"
                label={t('settlement_account_number')}
                name="merchantBankDetails[1].accountNumber"
                {...register("merchantBankDetails[1].accountNumber")}
                fullWidth
                disabled={!editmode}
                onKeyDown={handleNumberKeyDown}
                error={errors?.merchantBankDetails?.[1]?.accountNumber}
                helperText={errors?.merchantBankDetails?.[1]?.accountNumber?.message}
            />
          </Grid>
          <Grid item xs={2}>
            <Controller
              name="merchantBankDetails[1].bankCode"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <MDInput
                  type="text"
                  label={t('branch_code')}
                  {...field}
                  fullWidth
                  disabled={true}
                  error={errors?.merchantBankDetails?.[1]?.bankCode}
                  helperText={errors?.merchantBankDetails?.[1]?.bankCode?.message}
                />
              )}
            />
          </Grid> */}
        </Grid>
        <Divider variant="middle" style={{ marginBottom: '20px', marginTop: '20px' }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <MDInput type="text" label={t('expected_card_business_per_month')} name="expectedCardBusinessPerMonth"
                     {...register("expectedCardBusinessPerMonth")}
                     fullWidth
                     disabled={!editmode}
                     onKeyDown={handleNumberKeyDown}
                     error={errors?.expectedCardBusinessPerMonth}
                     required
                     helperText={errors.expectedCardBusinessPerMonth ? errors.expectedCardBusinessPerMonth.message : ''}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDInput type="text" label={t('average_bill_amount')} name="averageBillAmount"
                     {...register("averageBillAmount")}
                     fullWidth
                     disabled={!editmode}
                     onKeyDown={handleNumberKeyDown}
                     error={errors?.averageBillAmount}
                     helperText={errors.averageBillAmount ? errors.averageBillAmount.message : ''}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDInput type="text" label={t('turnover_years_1')} name="turnoverYear1"
                     {...register("turnoverYear1")}
                     fullWidth
                     disabled={!editmode}
                     onKeyDown={handleNumberKeyDown}
                     error={errors?.turnoverYear1}
                     helperText={errors.turnoverYear1 ? errors.turnoverYear1.message : ''}
            />
          </Grid>
        </Grid>
      </Grid>
  );
}

MerchantBankDetails.defaultProps = {
  merchant: false,
  editmode: false,
  setMerchant: false,
  type: 'updated'
}

MerchantBankDetails.propTypes = {
  merchant: PropTypes.any,
  editmode: PropTypes.bool,
  setMerchant: PropTypes.func,
  type: PropTypes.string
}

export default MerchantBankDetails;
